/**
 * studyIndex.js — понятие «исследование».
 *
 * До этого модуля программа такого слова не знала. Всё было разложено по
 * сериям: `_series`, `_imageIds`, `volume_<seriesUID>`. Номер исследования
 * (StudyInstanceUID) разбирался в воркере, доезжал до объекта серии — и на
 * этом умирал: по нему ничего не группировалось. Поле `_studyMap` в
 * loaderManager было объявлено и ни разу не заполнено.
 *
 * Для одного исследования на экране это неважно. Для сравнения двух — это
 * основа: врач выбирает не серию, а ИССЛЕДОВАНИЕ («КТ от 12 марта»), и уже
 * внутри него серию.
 *
 * Модуль чистый: ни импортов, ни DOM, ни обращений к Cornerstone. На вход —
 * список серий в том виде, в каком его отдают оба загрузчика; на выход —
 * карта исследований. Поэтому его можно проверить тестом, не поднимая
 * браузер, и он одинаково работает и для воркерного пути, и для запасного.
 */

/** Русские названия модальностей — врач читает не «MR», а «МРТ». */
const МОДАЛЬНОСТИ = {
  CT: 'КТ',
  MR: 'МРТ',
  PT: 'ПЭТ',
  NM: 'ОФЭКТ',
  US: 'УЗИ',
  XA: 'Ангио',
  CR: 'Рентген',
  DX: 'Рентген',
  RF: 'Рентгеноскопия',
  MG: 'Маммография',
  OT: 'Прочее',
};

/**
 * Собрать карту исследований из списка серий.
 *
 * Серии без номера исследования не выбрасываются: они попадают в корзину
 * 'unknown_study'. Выбросить их значило бы спрятать от врача снимки, которые
 * он видит в списке серий, — а хуже пропажи данных нет ничего.
 *
 * @param {Array<Object>} seriesList — объекты серий от любого из загрузчиков
 * @returns {Map<string, Object>} studyUID → сведения об исследовании
 */
export function buildStudyIndex(seriesList) {
  const исследования = new Map();

  for (const s of seriesList || []) {
    if (!s || !s.seriesUID) continue;
    const uid = s.studyUID || 'unknown_study';

    let ст = исследования.get(uid);
    if (!ст) {
      ст = {
        studyUID: uid,
        studyDate: '',
        studyDesc: '',
        patientName: '',
        patientId: '',
        modalities: [],
        series: [],
        images: 0,
      };
      исследования.set(uid, ст);
    }

    // Сведения берём у первой серии, где они есть: часть файлов может прийти
    // с пустыми полями, и затирать ими уже найденное нельзя.
    if (!ст.studyDate && s.studyDate) ст.studyDate = s.studyDate;
    if (!ст.studyDesc && s.studyDesc) ст.studyDesc = s.studyDesc;
    if (!ст.patientName && s.patientName && s.patientName !== 'Unknown') ст.patientName = s.patientName;
    if (!ст.patientId && s.patientId) ст.patientId = s.patientId;

    if (s.modality && !ст.modalities.includes(s.modality)) ст.modalities.push(s.modality);
    ст.series.push(s.seriesUID);
    ст.images += (s.images?.length || s.files?.length || 0);
  }

  return исследования;
}

/** ДДММГГГГ из DICOM (ГГГГММДД) в привычный вид. Мусор возвращаем как есть. */
export function formatStudyDate(dicomDate) {
  const d = String(dicomDate || '').trim();
  if (!/^\d{8}$/.test(d)) return d;
  return `${d.slice(6, 8)}.${d.slice(4, 6)}.${d.slice(0, 4)}`;
}

/** «MR» → «МРТ». Незнакомое оставляем как есть: врач поймёт, а выдумка — нет. */
export function formatModality(m) {
  const k = String(m || '').trim().toUpperCase();
  return МОДАЛЬНОСТИ[k] || k || '—';
}

/**
 * Строка для выпадающего списка: «МРТ • 12.03.2024 • головной мозг».
 * Пустые части опускаются, чтобы не было «МРТ •  • ».
 */
export function formatStudyTitle(study) {
  if (!study) return '';
  const части = [];
  if (study.modalities?.length) части.push(study.modalities.map(formatModality).join('/'));
  const дата = formatStudyDate(study.studyDate);
  if (дата) части.push(дата);
  if (study.studyDesc) части.push(study.studyDesc);
  if (!части.length) части.push('Исследование без описания');
  return части.join(' • ');
}

/**
 * Короткая подпись в угол окна: «МРТ 12.03.2024».
 * Описание сюда не идёт — оно бывает длиной в строку и закроет снимок.
 */
export function formatStudyBadge(study) {
  if (!study) return '';
  const мод = study.modalities?.length ? study.modalities.map(formatModality).join('/') : '';
  const дата = formatStudyDate(study.studyDate);
  return [мод, дата].filter(Boolean).join(' ');
}

/**
 * Отсортировать исследования: свежие сверху. Без даты — в конец, потому что
 * подставлять им сегодняшнюю значило бы соврать о порядке.
 */
export function sortStudiesByDate(studies) {
  const список = Array.isArray(studies) ? [...studies] : [...(studies?.values?.() || [])];
  return список.sort((a, b) => {
    const da = /^\d{8}$/.test(String(a?.studyDate || '')) ? String(a.studyDate) : '';
    const db = /^\d{8}$/.test(String(b?.studyDate || '')) ? String(b.studyDate) : '';
    if (da && db) return db.localeCompare(da);
    if (da) return -1;
    if (db) return 1;
    return 0;
  });
}
